'use client';

import {
  CheckCircle2,
  Clock,
  Circle,
  ArrowLeft,
  ArrowRight,
  Package,
  Globe,
  Container,
  LayoutDashboard,
  Sparkles,
  Eye,
  RefreshCw,
  ShieldCheck,
  Webhook,
  Building2,
  Puzzle,
  Brain,
  Activity,
  Zap,
  Network,
  Lock,
  FileSearch,
  AlertTriangle,
  BarChart3,
  Database,
  GitBranch,
  Cpu,
  Users,
  Radio,
  CloudCog,
  ScanSearch,
  TrendingUp,
} from 'lucide-react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { platformLink } from '@/lib/config';

type Status = 'shipped' | 'in-progress' | 'planned' | 'future' | 'vision';

interface Milestone {
  icon: React.ElementType;
  title: string;
  description: string;
  tag?: string;
}

interface Phase {
  period: string;
  label: string;
  status: Status;
  headline: string;
  sub: string;
  milestones: Milestone[];
}

const phases: Phase[] = [
  {
    period: 'Q3–Q4 2025',
    label: 'v1 - Foundation',
    status: 'shipped',
    headline: 'One engine. Three delivery layers.',
    sub: 'SDK, Cloudflare Worker, and self-hosted container - all powered by a single edge-safe detection core.',
    milestones: [
      {
        icon: Package,
        title: '@silker-ai/agent - SDK v1.0',
        description:
          'TypeScript SDK for Node.js & Next.js. Fetch hook + Express middleware. 427 tests green. Zero-config fail-open defaults. Ships as npm package.',
        tag: 'npm i @silker-ai/agent',
      },
      {
        icon: Globe,
        title: 'Cloudflare Worker',
        description:
          'Edge deployment with zero app-code changes. Same detection engine reused as a CF Worker - blocks threats before they hit origin. Deploys via Wrangler.',
        tag: 'wrangler deploy',
      },
      {
        icon: Container,
        title: 'Self-hosted container',
        description:
          'Docker reverse proxy for any backend stack - PHP, Python, Java, Go. One docker-compose, works on any VPS or cloud. Smoke-tested on SQLi / XSS / path traversal / prompt injection.',
        tag: 'docker compose up',
      },
      {
        icon: ShieldCheck,
        title: '@silker-ai/core - edge-safe engine',
        description:
          'V8-safe detection engine shared by all three delivery layers. Covers SQLi, XSS, path traversal, prompt injection, PII/secret leakage, file upload abuse, and rate limiting. No Node.js dependencies.',
        tag: 'edge-safe · ~0ms p99 overhead',
      },
    ],
  },
  {
    period: 'Q4 2025 – Q1 2026',
    label: 'v2 - Observability',
    status: 'shipped',
    headline: 'Real-time dashboard & telemetry pipeline.',
    sub: 'Every blocked request becomes structured telemetry. Security events land in the dashboard within milliseconds - never blocking the request path.',
    milestones: [
      {
        icon: LayoutDashboard,
        title: 'Security dashboard',
        description:
          'Threat timeline, blocked-by-type breakdown, IP ban management, data-leak alert feed, and per-app request analytics. Multi-app support with per-tenant row-level isolation.',
      },
      {
        icon: Activity,
        title: 'Async ingest pipeline',
        description:
          'Fire-and-forget event batching. Telemetry is delivered asynchronously via waitUntil/after() - never adds latency to the response path. Configurable sampling rate per plan.',
        tag: 'async · non-blocking · batchable',
      },
      {
        icon: Lock,
        title: 'Security hardening',
        description:
          'SHA-256 hashed API keys, row-level isolation per tenant, PII sanitisation client-side before any event leaves your server. API key only leaves your environment once.',
        tag: 'RLS · hashed keys · PII-safe',
      },
      {
        icon: Puzzle,
        title: 'Multi-method onboarding',
        description:
          'Dashboard detects install method (SDK / Worker / container) and generates pre-filled snippets with your API key and app_id. No copy-paste errors.',
      },
    ],
  },
  {
    period: 'Q2 2026',
    label: 'v3 - AI Detection Layer',
    status: 'shipped',
    headline: 'Semantic detection, AI Copilot & outbound inspection.',
    sub: 'Detection moves beyond signatures. Embedding-based models catch novel prompt-injection and jailbreak variants, every blocked threat ships with an AI-generated explanation and fix, and outbound responses are scanned for leaking secrets in real time.',
    milestones: [
      {
        icon: Brain,
        title: 'Semantic threat detection',
        description:
          'Embedding-based detection of prompt injection, jailbreaks, and obfuscated payloads that slip past signature rules. Runs alongside the deterministic engine - flags novel attack variants with no hand-written rules, scored against a learned threat manifold.',
        tag: 'embeddings · shipped',
      },
      {
        icon: Sparkles,
        title: 'AI Copilot - explain & fix',
        description:
          'Every blocked threat is enriched with a plain-English explanation and a concrete code fix - directly in the dashboard. Powered by LLM reasoning over structured telemetry context.',
        tag: 'shipped',
      },
      {
        icon: Eye,
        title: 'Response inspection',
        description:
          'Scans outbound responses for PII patterns (email, phone, SSN), API key fragments (sk_, api_key=), and secret formats before they reach the client. Generates data_leak events.',
        tag: 'shipped',
      },
      {
        icon: Cpu,
        title: 'Streaming LLM guardrails',
        description:
          'Token-level inspection of streamed model output - detects jailbreak success, prompt exfiltration, and PII leaks mid-stream and cuts the response before the payload completes. Sub-token latency on the edge runtime.',
        tag: 'streaming · shipped',
      },
    ],
  },
  {
    period: 'Q3 2026',
    label: 'v4 - Intelligence',
    status: 'planned',
    headline: 'Adaptive rules, webhooks & API learning.',
    sub: 'Silker stops being reactive and starts being predictive. Rules update remotely, anomaly baselines are learned automatically, and alerts push to your existing tooling.',
    milestones: [
      {
        icon: RefreshCw,
        title: 'Live config sync',
        description:
          'SDK and Worker periodically fetch feature flags, ban lists, and sampling rates from the platform - zero redeploys needed. Changes propagate within one polling interval.',
      },
      {
        icon: Webhook,
        title: 'SIEM & alerting integrations',
        description:
          'Push structured security events to Datadog, Splunk, PagerDuty, Slack, or any webhook endpoint. Configurable severity filters, retry logic, and HMAC signing.',
      },
      {
        icon: Brain,
        title: 'API schema learning',
        description:
          'Silker observes your normal traffic for N days to build a per-app baseline of endpoint shapes, method distributions, and parameter types. Anomalies surface automatically - no manual rules.',
        tag: 'ML-assisted · per-app baseline',
      },
      {
        icon: FileSearch,
        title: 'CI/CD security gate',
        description:
          'GitHub Action and CLI tool that runs Silker\'s detection suite against your test suite before every deploy. Catches new attack surfaces before they reach production.',
        tag: 'github actions · pre-deploy',
      },
    ],
  },
  {
    period: 'Q4 2026',
    label: 'v5 - Platform',
    status: 'future',
    headline: 'Enterprise self-host, compliance & multi-framework.',
    sub: 'The full Silker platform - deployable on-prem, SOC 2 aligned, with SSO and a marketplace of framework adapters.',
    milestones: [
      {
        icon: Building2,
        title: 'Enterprise self-host',
        description:
          'On-prem deployment with SSO/SAML, audit logs, HA clustering with Redis shared state, SLA, and a dedicated customer success channel. Air-gap mode for regulated industries.',
        tag: 'on-prem · HA · air-gap',
      },
      {
        icon: Package,
        title: 'Multi-framework adapter library',
        description:
          'First-party adapters for Remix, SvelteKit, Astro, Express, FastAPI, Laravel, and Spring Boot - same @silker-ai/core, framework-native install patterns.',
      },
      {
        icon: BarChart3,
        title: 'Compliance reporting',
        description:
          'Automated evidence collection for SOC 2, GDPR Article 32, HIPAA, and ISO 27001. Generates audit-ready PDF reports from your real threat telemetry.',
        tag: 'SOC 2 · GDPR · HIPAA',
      },
      {
        icon: Database,
        title: 'Threat intelligence export',
        description:
          'Export your full threat history as structured STIX/TAXII feeds. Integrate with existing SIEM/SOAR pipelines or feed into your own ML models.',
        tag: 'STIX · TAXII · API',
      },
    ],
  },
  {
    period: '2027+',
    label: 'Vision - AI Security Fabric',
    status: 'vision',
    headline: 'The security layer for the AI-native internet.',
    sub: 'Every AI app in the world ships with runtime protection by default. Silker becomes the standard - like TLS, but for AI-generated attack surfaces.',
    milestones: [
      {
        icon: Network,
        title: 'Cross-customer threat intelligence network',
        description:
          'Aggregated, anonymised threat signals from all Silker-protected apps create a shared intelligence layer. New attack patterns detected on one app are immediately blocked across the network.',
        tag: 'federated · privacy-preserving',
      },
      {
        icon: Cpu,
        title: 'LLM behaviour monitoring',
        description:
          'Continuous monitoring of model outputs for jailbreak success, prompt exfiltration, hallucination-driven data leaks, and adversarial fine-tuning signals - not just inputs.',
        tag: 'model-level · output analysis',
      },
      {
        icon: GitBranch,
        title: 'AI supply chain security',
        description:
          'Tracks third-party LLM SDKs, model versions, and vector stores your app depends on. Alerts on CVEs, prompt injection vectors in model system prompts, and model substitution attacks.',
        tag: 'sbom · model provenance',
      },
      {
        icon: ScanSearch,
        title: 'Autonomous red-teaming',
        description:
          'On-demand AI-driven penetration testing that generates novel attack variations specific to your app\'s architecture. Continuous, not point-in-time.',
        tag: 'AI-driven · continuous',
      },
      {
        icon: Radio,
        title: 'Real-time threat market',
        description:
          'Security researchers submit novel attack signatures; Silker validates, prices, and distributes them to all protected apps within minutes. Bug bounty meets threat intel marketplace.',
      },
      {
        icon: CloudCog,
        title: 'Zero-trust AI gateway',
        description:
          'Full API gateway mode: request authentication, per-user rate limits, token budget enforcement, and tool-call authorization for agentic AI workflows. Security and routing in one layer.',
        tag: 'agentic AI · tool-call auth',
      },
      {
        icon: TrendingUp,
        title: 'Security posture score',
        description:
          'A single, auditable score (0–100) representing your app\'s real-time security posture. Embeds in READMEs, investor decks, and customer security questionnaires.',
      },
      {
        icon: Users,
        title: 'Developer security marketplace',
        description:
          'Community plugins, detection rules, and framework adapters published by the ecosystem. Revenue sharing for top contributors. Silker as the platform, not just a product.',
        tag: 'ecosystem · revenue share',
      },
    ],
  },
];

const statusConfig: Record<Status, {
  dot: string;
  badge: string;
  line: string;
  cardBorder: string;
  cardBg: string;
  iconBg: string;
  iconBorder: string;
  iconColor: string;
  tagColor: string;
  label: string;
  Icon: React.ElementType;
}> = {
  shipped: {
    dot: 'bg-emerald-400',
    badge: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
    line: 'bg-emerald-500/30',
    cardBorder: 'border-emerald-500/15',
    cardBg: 'bg-emerald-500/5',
    iconBg: 'bg-emerald-500/10',
    iconBorder: 'border-emerald-500/20',
    iconColor: 'text-emerald-400',
    tagColor: 'text-emerald-500/70 bg-emerald-500/5 border-emerald-500/15',
    label: 'Shipped',
    Icon: CheckCircle2,
  },
  'in-progress': {
    dot: 'bg-rose-400 animate-pulse',
    badge: 'text-rose-400 bg-rose-500/10 border-rose-500/30',
    line: 'bg-rose-500/30',
    cardBorder: 'border-rose-500/20',
    cardBg: 'bg-rose-500/5',
    iconBg: 'bg-rose-500/10',
    iconBorder: 'border-rose-500/20',
    iconColor: 'text-rose-400',
    tagColor: 'text-rose-400/70 bg-rose-500/5 border-rose-500/15',
    label: 'In progress',
    Icon: Clock,
  },
  planned: {
    dot: 'bg-blue-400',
    badge: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
    line: 'bg-blue-500/20',
    cardBorder: 'border-blue-500/10',
    cardBg: 'bg-blue-500/5',
    iconBg: 'bg-blue-500/10',
    iconBorder: 'border-blue-500/20',
    iconColor: 'text-blue-400',
    tagColor: 'text-blue-400/70 bg-blue-500/5 border-blue-500/15',
    label: 'Planned',
    Icon: Circle,
  },
  future: {
    dot: 'bg-violet-400',
    badge: 'text-violet-400 bg-violet-500/10 border-violet-500/30',
    line: 'bg-violet-500/20',
    cardBorder: 'border-violet-500/10',
    cardBg: 'bg-violet-500/5',
    iconBg: 'bg-violet-500/10',
    iconBorder: 'border-violet-500/20',
    iconColor: 'text-violet-400',
    tagColor: 'text-violet-400/70 bg-violet-500/5 border-violet-500/15',
    label: 'Future',
    Icon: Zap,
  },
  vision: {
    dot: 'bg-amber-400',
    badge: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    line: 'bg-amber-500/20',
    cardBorder: 'border-amber-500/10',
    cardBg: 'bg-amber-500/5',
    iconBg: 'bg-amber-500/10',
    iconBorder: 'border-amber-500/20',
    iconColor: 'text-amber-400',
    tagColor: 'text-amber-400/70 bg-amber-500/5 border-amber-500/15',
    label: 'Vision',
    Icon: Sparkles,
  },
};

const stats = [
  { value: '427', label: 'Detection tests passing' },
  { value: '~0ms', label: 'p99 overhead per request' },
  { value: '3', label: 'Delivery layers shipped' },
  { value: '6', label: 'Attack classes detected' },
];

export default function RoadmapPage() {
  return (
    <div className="min-h-screen bg-black">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-900/10 via-black to-black" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-rose-500/5 rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-violet-500/3 rounded-full blur-3xl" />
      </div>

      <Header />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors group mb-8"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back</span>
        </Link>

        {/* Hero */}
        <div className="mb-16">
          <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-6">
            <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700" />
            Product roadmap · Updated June 2026
            <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-tight">
            <span className="bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent">
              Runtime security for<br />the AI-native internet.
            </span>
          </h1>
          <p className="text-base text-neutral-400 max-w-2xl leading-relaxed mb-10">
            AI apps ship faster than security can keep up. Silker AI is the runtime protection layer
            that installs in one line and grows into the security fabric for every AI application on the web.
            This is where we are and where we're going.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {stats.map((s) => (
              <div key={s.label} className="p-4 border border-white/5 bg-neutral-900/30">
                <div className="text-2xl font-bold text-white font-mono mb-1">{s.value}</div>
                <div className="text-xs text-neutral-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 mb-14">
          {(['shipped', 'in-progress', 'planned', 'future', 'vision'] as Status[]).map((s) => {
            const c = statusConfig[s];
            const Icon = c.Icon;
            return (
              <span key={s} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 border ${c.badge}`}>
                <Icon className="w-3 h-3" />
                {c.label}
              </span>
            );
          })}
        </div>

        {/* Timeline */}
        <div className="relative">
          <div className="absolute left-[19px] top-2 bottom-2 w-px bg-gradient-to-b from-emerald-500/50 via-rose-500/30 via-blue-500/20 via-violet-500/20 to-amber-500/30" />

          <div className="space-y-16">
            {phases.map((phase, phaseIdx) => {
              const c = statusConfig[phase.status];
              const HeaderIcon = c.Icon;
              return (
                <div key={phaseIdx} className="relative pl-12">
                  <div className={`absolute left-0 top-1 w-[38px] h-[38px] rounded-none ${c.iconBg} border ${c.iconBorder} flex items-center justify-center`}>
                    <HeaderIcon className={`w-4 h-4 ${c.iconColor}`} />
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mb-1">
                    <span className={`text-xs font-mono font-bold px-2 py-0.5 border ${c.badge}`}>
                      {phase.period}
                    </span>
                    <span className="text-xs text-neutral-600 font-mono uppercase tracking-widest">
                      {phase.label}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">{phase.headline}</h2>
                  <p className="text-sm text-neutral-500 mb-6 max-w-2xl leading-relaxed">{phase.sub}</p>

                  <div className="grid sm:grid-cols-2 gap-3">
                    {phase.milestones.map((m, mIdx) => {
                      const MIcon = m.icon;
                      return (
                        <div
                          key={mIdx}
                          className={`p-5 rounded-none border ${c.cardBorder} ${c.cardBg} hover:brightness-110 transition-all duration-200`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`shrink-0 w-8 h-8 rounded-none ${c.iconBg} border ${c.iconBorder} flex items-center justify-center mt-0.5`}>
                              <MIcon className={`w-4 h-4 ${c.iconColor}`} />
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-sm font-semibold text-white mb-1 leading-tight">{m.title}</h3>
                              <p className="text-xs text-neutral-500 leading-relaxed mb-2">{m.description}</p>
                              {m.tag && (
                                <code className={`text-[10px] font-mono px-2 py-0.5 border ${c.tagColor}`}>
                                  {m.tag}
                                </code>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Investor CTA */}
        <div className="mt-20 space-y-4">
          <div className="rounded-none border border-amber-500/20 bg-amber-500/5 p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 rounded-none bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                <TrendingUp className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white mb-1">For investors</h2>
                <p className="text-sm text-neutral-400 leading-relaxed max-w-2xl">
                  AI is eating software. Every new app ships with an LLM, a vector store, and a tool-calling agent -
                  and zero runtime security. Silker is building the security primitive that every AI app will need,
                  starting with a one-line SDK and growing into the trust layer for the AI-native internet.
                  We are pre-Series A, onboarding design partners, and shipping fast.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <Link
                href="/investors"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-black rounded-none text-sm font-medium hover:bg-neutral-100 transition-colors"
              >
                Investor deck
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-none border border-amber-500/30 text-amber-400 text-sm font-medium hover:bg-amber-500/10 transition-colors"
              >
                Get in touch
              </Link>
            </div>
          </div>

          <div className="rounded-none border border-neutral-800/50 bg-neutral-950/50 p-8 text-center">
            <h2 className="text-lg font-semibold text-white mb-2">Start protecting your app today</h2>
            <p className="text-sm text-neutral-400 mb-6 max-w-xl mx-auto">
              One npm install. Zero-config defaults. Your first app is free.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/docs/quickstart"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-none border border-neutral-700 text-white text-sm font-medium hover:border-rose-500/40 hover:bg-rose-500/5 transition-colors w-full sm:w-auto"
              >
                Quickstart guide
              </Link>
              <a
                href={platformLink.register()}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-black rounded-none text-sm font-medium hover:bg-neutral-100 transition-colors w-full sm:w-auto"
              >
                Create free account
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
